import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import util from "@utils/utils";
import { MOMENT_FOREVER_DAYS, DATE_FORMAT_STRING } from '@constants'
import { FileCard } from '../FileCard';
import { LoadingDiv } from '@components/LoadingDiv';
import { ReactComponent as SparkSvg } from '@images/icons/spark.svg';
import { Activity, ChevronRight, Star, Printer, Download, Grid, Menu, ChevronsUp, ChevronsDown, Database, Search as SearchIcon } from 'react-feather';
import { LoadingCardDiv } from '../LoadingCardDiv';
import { all } from 'axios';
import { PolicyChangeAnalysis } from '../PolicyChangeAnalysis';
import useCreateSearchFast from '@hooks/useCreateSearchFast';
import { Zap, Clock, FileText, Calendar, Download as DownloadIcon, Star as StarIcon, GitCompare, TrendingUp, Building2 } from 'lucide-react';
import { MindTree } from '../MindTree';
import { Timeline } from '../Timeline';
import useGetPolicyJiedu from '@hooks/useGetPolicyJiedu';
import useGetPolicyTimelineMindmap from '@views/Policy/hooks/useGetPolicyTimelineMindmap';
import useGetPolicyTimeline from '@views/Policy/hooks/useGetPolicyTimeline';
import { MarkBtn } from '../MarkBtn';
import { useConfig } from '../../../../contexts/ConfigContext';
import { useStore } from '../../StoreContext';
import { useNavigate } from 'react-router-dom';


// 新增：增强版文件卡片组件，复用快速搜索样式
const EnhancedFileCard = ({ item, index, setFileName, setFileId, getPdfAction, setMdTxt, savedFoldListData, setMarkData, isSelected, onSelect, input, pageNum, pageSize, mindmapVisible, hightlight, score, state, actions }) => {
    const { theme } = useConfig();
    const navigate = useNavigate();
    

    // 标签处理 - 不分割，直接使用完整的 theme
    const tags = item?.policy_theme ? [item.policy_theme] : [];
    // 时间格式化
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = dateString.split('T')[0]; // Remove time part if present
        const parts = date.split('-');
        if (parts.length === 3) {
            const year = parts[0];
            const month = parseInt(parts[1], 10); // Remove leading zero
            const day = parseInt(parts[2], 10); // Remove leading zero
            return `${year}年${month}月${day}日`;
        }
        return dateString;
    };
    const dateStr = formatDate(item?.policy_time);
    // 来源
    const source = item?.from_organization || '';
    // 类型
    const type = item?.large_policy_class;
    // 标题 - 优先使用 highlights.policy_title 中的高亮内容，如果没有则使用原始标题
    let title = '无标题';
    if (item?.highlights?.policy_title) {
        const t = item.highlights.policy_title;
        if (Array.isArray(t) && t.length > 0) {
            title = t[0];
        } else if (typeof t === 'string') {
            title = t;
        }
    } else if (item?.policy_title || item?.policy_name) {
        title = item.policy_title || item.policy_name;
    }
    // 摘要 - 优先使用 highlights.full_content，如果没有则使用 related_content
    let summary = '暂无详细内容';
    let fullSummary = '暂无详细内容';
    let hasHighlights = false;
    
    if (item?.highlights?.full_content) {
        const c = item.highlights.full_content;
        if (Array.isArray(c) && c.length > 0) {
            hasHighlights = true;
            // 默认显示前3行，每行用换行符分隔
            const previewLines = c.slice(0, 3);
            summary = previewLines.join('<br><br>');
            // 全部内容，每行用换行符分隔
            fullSummary = c.join('<br><br>');
        } else if (typeof c === 'string') {
            hasHighlights = true;
            summary = c;
            fullSummary = c;
        }
    } else if (item?.related_content) {
        summary = item.related_content;
        fullSummary = item.related_content;
    }
    // id
    const id = item?.source_id || index;
    // 相关按钮功能
    const handleCompareClick = () => {
        // 先更新 state
        if(!state?.some(i=>i?.source_id === item?.source_id)){
            actions.updateValue([item,...state])
        }
        
        // 在当前页面导航
        navigate(`/policy/compareSelect?clickFrom=${item?.source_id}&returnUrl=${encodeURIComponent(window.location.href)}`);
    };

    const handleDownload = () => {
        getPdfAction({policy_info:[{policy_title:item?.policy_title || item?.policy_name,policy_id:item?.source_id}]})
    }

    // 使用安全的URL构建方法
    const filePage = util.buildPolicyFileUrl(
        item?.policy_title || item?.policy_name, 
        item?.source_id, 
        input || ''
    );
    
    // 新增：政策解读功能
    const { getJieduWithCache } = useGetPolicyJiedu();
    const [jieduContent, setJieduContent] = useState(null);
    const [jieduVisible, setJieduVisible] = useState(false);
    const [jieduLoading, setJieduLoading] = useState(false);
    
    // 新增：正文展开状态
    const [summaryExpanded, setSummaryExpanded] = useState(false);
    
    // 新增：文章脉络和时间线分析功能
    const { getPolicyTimelineMindmapAction, policyTimelineMindmapLoading } = useGetPolicyTimelineMindmap();
    const [mindTreeData, setMindTreeData] = useState(null);
    const [timelineVisible, setTimelineVisible] = useState(false);
    const [mindTreeVisible, setMindTreeVisible] = useState(false);
    
    // 跳转详情页
    
    return (
        <div
            className={`group hover:shadow-lg transition-all duration-300 border-l-4 ${isSelected ? 'border-primary border-2' : ''} bg-white/80 backdrop-blur-sm rounded-lg p-6 w-full cursor-pointer`}
            style={{
                borderLeftColor: isSelected ? theme.primary : theme.primary,
                '--tw-ring-color': theme.primaryHover,
            }}
            onClick={async e => {
                // 如果点击的是按钮、链接、输入框或其子元素，不处理卡片点击逻辑
                if (
                    e.target.tagName === 'BUTTON' ||
                    e.target.closest('button') ||
                    e.target.tagName === 'A' ||
                    e.target.closest('a') ||
                    e.target.tagName === 'INPUT' ||
                    e.target.closest('input')
                ) return;
                
                // 点击卡片空白区域：选中卡片 + 调用解读接口 + 展开正文
                onSelect && onSelect(item);
                
                // 切换正文展开状态
                setSummaryExpanded(!summaryExpanded);
                
                // 新增：如果是政策解读类型，不请求解读接口也不显示解读内容
                if (item?.large_policy_class === '政策解读') {
                    setJieduContent(null);
                    setJieduVisible(false);
                    return;
                }
                
                // 只在未显示解读时请求，且确保其他功能已关闭
                if (!jieduVisible && !jieduLoading && !mindTreeVisible && !timelineVisible) {
                    setJieduLoading(true);
                    const policyTitle = item?.policy_title || item?.policy_name;
                    // 使用文件自己的时间
                    const policyTime = item?.policy_time;
                    if (policyTitle) {
                        const result = await getJieduWithCache(policyTitle, policyTime);
                        // 只有有内容时才显示
                        if (result && ((Array.isArray(result) && result.length > 0) || (typeof result === 'object' && Object.keys(result).length > 0))) {
                            setJieduContent(result);
                            setJieduVisible(true);
                        } else {
                            setJieduContent(null);
                            setJieduVisible(false);
                        }
                    } else {
                        setJieduContent(null);
                        setJieduVisible(false);
                    }
                    setJieduLoading(false);
                } else if (jieduVisible) {
                    // 再次点击收起
                    setJieduVisible(false);
                }
            }}
        >
            <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center gap-2">
                    {/* 暂时注释掉复选框，目前不需要
                    <input 
                        type="checkbox" 
                        className="checkbox checkbox-sm" 
                        checked={isSelected} 
                        onChange={(e) => {
                            e.stopPropagation();
                            onSelect && onSelect(item);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                    */}
                    <span className="flex items-center justify-center w-8 h-8 text-white font-bold rounded-full text-sm" style={{
                        background: `linear-gradient(to bottom right, ${theme.primary}, #475569)`
                    }}>{(pageNum - 1) * pageSize + index + 1}</span>
                </div>
                                  <span className="border text-slate-700 rounded px-2 py-0.5 text-xs flex items-center" style={{
                      borderColor: theme.primary,
                      backgroundColor: `${theme.primary}20`,
                      color: theme.primary
                  }}>{type}</span>
                <span className="flex items-center gap-1 text-sm text-gray-500"><Calendar className="w-3 h-3" />{dateStr}</span>
                
            </div>
            <div className="flex items-center mb-1">
                <h3 className="text-lg font-semibold text-gray-900 transition-colors line-clamp-2 relative z-10 inline-block cursor-pointer" style={{
                    '--tw-ring-color': theme.primaryHover,
                }} onClick={e => {
                    e.stopPropagation();
                    setFileName(item?.policy_title || item?.policy_name);
                    window.open(filePage, '_blank');
                }} dangerouslySetInnerHTML={{ __html: title }} />
                <div className="flex-1" />
            </div>
            <div className="text-gray-500 text-sm mb-1">政策来源：{source}</div>
            <div className={`text-gray-700 text-base mb-2 ${summaryExpanded ? '' : 'line-clamp-3'}`}>
                <div dangerouslySetInnerHTML={{ __html: summaryExpanded ? fullSummary : summary }} />
                {hasHighlights && item?.highlights?.full_content && Array.isArray(item.highlights.full_content) && item.highlights.full_content.length > 3 && (
                    <button 
                        className="text-sm ml-2 underline"
                        style={{
                            color: theme.primary,
                            '--tw-ring-color': theme.primaryHover,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSummaryExpanded(!summaryExpanded);
                        }}
                    >
                        {summaryExpanded ? '收起' : '展开'}
                    </button>
                )}
            </div>
            {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                    {tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="rounded bg-slate-100 text-slate-600 px-2 py-0.5 text-xs">{tag}</span>
                    ))}
                </div>
            )}
            <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100 items-center">
                <button 
                    className={`flex items-center px-2 py-1 rounded text-sm font-bold`}
                    style={{
                        color: theme.primary,
                        backgroundColor: mindTreeVisible ? `${theme.primary}20` : 'transparent',
                        '--tw-ring-color': theme.primaryHover,
                    }}
                    onClick={async (e) => {
                        e.stopPropagation();
                        if (!mindTreeVisible) {
                            setMindTreeVisible(true);
                            setTimelineVisible(false);
                            setJieduVisible(false);
                            // 调用文章脉络接口
                            const policyTitle = item?.policy_title || item?.policy_name;
                            const policyId = item?.source_id;
                            if (policyTitle) {
                                const result = await getPolicyTimelineMindmapAction({ policy_title: policyTitle, policy_id: policyId });
                                setMindTreeData(result);
                            }
                        } else {
                            setMindTreeVisible(false);
                        }
                    }}
                >
                    <GitCompare className="w-4 h-4 mr-1" />
                    文章脉络
                    {policyTimelineMindmapLoading && <span className="loading loading-spinner loading-xs ml-1"></span>}
                </button>
                <button 
                    className={`flex items-center px-2 py-1 rounded text-sm font-bold`}
                    style={{
                        color: theme.primary,
                        backgroundColor: timelineVisible ? `${theme.primary}20` : 'transparent',
                        '--tw-ring-color': theme.primaryHover,
                    }}
                    onClick={async (e) => {
                        e.stopPropagation();
                        setTimelineVisible(!timelineVisible);
                        setMindTreeVisible(false);
                        setJieduVisible(false);
                    }}
                >
                    <Calendar className="w-4 h-4 mr-1" />
                    政策时间线分析
                </button>
                <button className="flex items-center px-2 py-1 rounded text-sm font-bold" style={{
                    color: theme.primary,
                    '--tw-ring-color': theme.primaryHover,
                }} onClick={handleCompareClick}><GitCompare className="w-4 h-4 mr-1" />政策对比</button>
                <div className="ml-auto flex gap-2">
                    <button className="text-gray-600 px-2 py-1 rounded text-sm" style={{
                        '--tw-ring-color': theme.primaryHover,
                    }} onClick={() => {
                        setFileName(item?.policy_title || item?.policy_name);
                        setFileId(item?.source_id);
                        window.open(filePage, '_blank');
                    }}>原文</button>
                    <button className="flex items-center text-gray-600 px-2 py-1 rounded text-sm" style={{
                        '--tw-ring-color': theme.primaryHover,
                    }} onClick={handleDownload}><DownloadIcon className="w-4 h-4 mr-1" />下载</button>
                    <MarkBtn
                        savedFoldListData={savedFoldListData}
                        source_id={item?.source_id}
                        setMarkData={setMarkData}
                        type={undefined}
                        noTxt={false}
                        color={"text-gray-600 px-2 py-1 rounded text-sm"}
                        style={{
                            '--tw-ring-color': theme.primaryHover,
                        }}
                    />
                </div>
            </div>
            {/* 展开内容区 */}
            {jieduVisible && jieduContent && <div className="mt-4 relative z-0">
                <div className="mb-4 p-4 bg-white rounded shadow border border-gray-200">
                    <div className="flex items-center gap-2 mb-4">
                        <FileText className="w-4 h-4" style={{ color: theme.primary }} />
                        <span className="font-semibold text-slate-700">相关解读</span>
                        {/* {(() => {
                            // 计算解读数量
                            let count = 0;
                            if (Array.isArray(jieduContent)) {
                                count = jieduContent.length;
                            } else if (jieduContent && typeof jieduContent === 'object') {
                                if (jieduContent.interpretations && Array.isArray(jieduContent.interpretations)) {
                                    count = jieduContent.interpretations.length;
                                } else {
                                    count = 1;
                                }
                            }
                            return count > 0 ? (
                                <span className="text-white text-xs font-bold rounded px-2 py-0.5" style={{
                                    backgroundColor: theme.primary
                                }}>
                                    {count}篇文件
                                </span>
                            ) : null;
                        })()} */}
                    </div>
                    <div className="space-y-4">
                        {(() => {
                            let interpretations = [];
                            if (Array.isArray(jieduContent)) {
                                interpretations = jieduContent.slice(0, 3);
                            } else if (jieduContent && typeof jieduContent === 'object') {
                                if (jieduContent.interpretations && Array.isArray(jieduContent.interpretations)) {
                                    interpretations = jieduContent.interpretations.slice(0, 3);
                                } else {
                                    interpretations = [jieduContent];
                                }
                            }
                            if (interpretations.length === 0) {
                                return <div className="text-gray-500 text-sm">暂无解读内容</div>;
                            }
                            return interpretations.map((interpretation, interpIndex) => {
                                // 标题使用最外层的 policy_title，正文使用 highlights.full_content
                                let titleHtml = interpretation.policy_title || '无标题';
                                let contentHtml = '暂无详细内容';
                                if (interpretation.highlights) {
                                  const c = interpretation.highlights.full_content;
                                  if (Array.isArray(c) && c.length > 0) {
                                    contentHtml = c[0];
                                  } else if (typeof c === 'string') {
                                    contentHtml = c;
                                  }
                                }
                                // 使用接口返回的时间和机构字段
                                const interpretationDate = interpretation.policy_time || interpretation.date || '';
                                const interpretationSource = interpretation.from_organization || source || '';
                                let content = '暂无详细内容';
                                if (interpretation.content) {
                                    content = typeof interpretation.content === 'string' ? interpretation.content : JSON.stringify(interpretation.content);
                                } else if (interpretation.full_content) {
                                    content = typeof interpretation.full_content === 'string' ? interpretation.full_content : JSON.stringify(interpretation.full_content);
                                } else if (interpretation.highlights) {
                                    content = typeof interpretation.highlights === 'string' ? interpretation.highlights : JSON.stringify(interpretation.highlights);
                                }
                                const formatDate = (dateString) => {
                                    if (!dateString) return '';
                                    const date = dateString.split('T')[0];
                                    const parts = date.split('-');
                                    if (parts.length === 3) {
                                        const year = parts[0];
                                        const month = parseInt(parts[1], 10);
                                        const day = parseInt(parts[2], 10);
                                        return `${year}年${month}月${day}日`;
                                    }
                                    return dateString;
                                };
                                return (
                                    <div key={interpIndex} className="bg-white/60 rounded-md p-4 border" style={{
                                    borderColor: `${theme.primary}30`
                                }}>
                                        <div className="flex items-start gap-3 mb-3">
                                            <span className="flex items-center justify-center w-6 h-6 border-2 text-xs font-bold rounded-full" style={{
                                    borderColor: theme.primary,
                                    color: theme.primary,
                                    backgroundColor: `${theme.primary}20`
                                }}>
                                                {interpIndex + 1}
                                            </span>
                                            <div className="flex-1">
                                                <h5 
                                                    className="font-semibold text-slate-800 text-sm mb-1 cursor-pointer transition-colors"
                                style={{
                                    '--tw-ring-color': theme.primaryHover,
                                }} 
                                                    dangerouslySetInnerHTML={{ __html: titleHtml }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const interpretationTitle = titleHtml.replace(/<[^>]*>/g, ''); // 移除HTML标签
                                                        
                                                        // 直接使用解读接口返回的policy_id字段
                                                        const interpretationId = interpretation.policy_id || '';
                                                        
                                                        
                                                        // 使用 util.buildPolicyFileUrl 构建安全的 URL
                                                        const interpretationFilePage = util.buildPolicyFileUrl(
                                                            interpretationTitle,
                                                            interpretationId,
                                                            input ?? '',
                                                            window.location.href
                                                        );
                                                        
                                                        // 如果没有ID，显示警告但仍然尝试打开
                                                        if (!interpretationId) {
                                                            console.warn('解读文章缺少policy_id，可能无法正确获取数据:', interpretationTitle);
                                                        }
                                                        
                                                        window.open(interpretationFilePage, '_blank');
                                                    }}
                                                />
                                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                                    <div className="flex items-center gap-1">
                                                        <Building2 className="w-3 h-3" />
                                                        <span>政策来源：{interpretationSource}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        <span>{formatDate(interpretationDate)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-700 leading-relaxed line-clamp-3" dangerouslySetInnerHTML={{ __html: contentHtml }} />
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </div>
            </div>}
            
            {/* 文章脉络内容区 */}
            {mindTreeVisible && <div className="mt-4 relative z-0">
                <div className="mb-4 p-4 bg-white rounded shadow border border-gray-200">
                    <MindTree
                        fileName={item?.policy_title || item?.policy_name}
                        fileId={item?.source_id}
                        setFileName={setFileName}
                        setMdTxt={setMdTxt}
                    />
                </div>
            </div>}
            
            {/* 政策时间线分析内容区 */}
            <div className={`mt-4 relative z-0 ${timelineVisible ? '' : 'hidden'}`}>
                <div className="mb-4 p-4 bg-white rounded shadow border border-gray-200">
                    <Timeline
                        currentPolicy={item}
                        mode="small"
                        isInModal={false}
                    />
                </div>
            </div>
        </div>
    );
};

// 工具函数：清理文本，去除标点符号和空格
const cleanText = (text) => {
    return text?.replace(/[^\w\u4e00-\u9fa5]/g, '').toLowerCase() || '';
};

// 工具函数：清理政策标题，去除书名号、括号、发文号等
const cleanPolicyName = (name) => {
    if (!name) return '';

    return name
        // 去除书名号
        .replace(/[《》]/g, '')
        // 去除中英文括号及其内容
        .replace(/\([^)]*\)/g, '')
        .replace(/（[^）]*）/g, '')
        .replace(/\[[^\]]*\]/g, '')
        .replace(/【[^】]*】/g, '')
        // 去除常见发文号格式（如：国发〔2023〕12号、京政发[2023]5号等）
        .replace(/[京沪津渝川粤苏浙鲁豫湘鄂赣皖闽桂黔滇琼陕甘青宁新藏蒙辽吉黑国][^〔\[]*[〔\[][^〕\]]*[〕\]][^\d]*\d+号/g, '')
        // 去除其他常见发文号格式
        .replace(/\w+发\[[^\]]*\]\d+号/g, '')
        .replace(/\w+〔\d+〕\d+号/g, '')
        // 去除空格和特殊字符
        .replace(/\s+/g, '')
        .replace(/[^\w\u4e00-\u9fa5]/g, '')
        .toLowerCase()
        .trim();
};

// 工具函数：分组相同内容的政策
const groupSimilarPolicies = (data) => {
    if (!data || data.length === 0) return [];

    const result = [];
    const processedIndices = new Set();

    for (let i = 0; i < data.length; i++) {
        if (processedIndices.has(i)) continue;

        const currentItem = data[i];
        const currentCleanContent = cleanText(currentItem.related_content);
        const currentCleanTitle = cleanPolicyName(currentItem.policy_title || currentItem.policy_name);
        const group = [currentItem];

        // 查找相邻的相同内容或相同标题
        for (let j = i + 1; j < data.length; j++) {
            if (processedIndices.has(j)) break;

            const compareItem = data[j];
            const compareCleanContent = cleanText(compareItem.related_content);
            const compareCleanTitle = cleanPolicyName(compareItem.policy_title || compareItem.policy_name);

            // 判断内容相同或标题相同（且都不为空）
            const contentMatch = currentCleanContent === compareCleanContent && currentCleanContent.length > 0;
            const titleMatch = currentCleanTitle === compareCleanTitle && currentCleanTitle.length > 0;

            if (contentMatch || titleMatch) {
                group.push(compareItem);
                processedIndices.add(j);
            } else {
                break; // 只处理相邻的相同内容
            }
        }

        processedIndices.add(i);
        result.push({
            isGroup: group.length > 1,
            items: group,
            originalIndex: i
        });
    }

    return result;
};

export const MainSearchResults = (props) => {
    const {
        fileName, setMdTxt, getPdfAction, getPdfLoading, setFileId, input, allData,
        loading, data, setQuery, setFileName,
        selectedCards, setSelectedCards, setMarkData, savedFoldListData, stateGridRelated, setStateGridRelated, searchK,
        searchOrg, // 新增
        filterTags, // 新增：筛选参数
        queryArgs, // 新增：完整的查询参数
        tab, // 新增：tab参数（保留但不用于前端过滤）
        state, // 新增：StoreContext的state
        actions, // 新增：StoreContext的actions
    } = props
    const [mouseDown, setMouseDown] = useState(false);
    const [selecting, setSelecting] = useState(false);
    // const [upBtnVisible,setUpBtnVisible] = useState(false)
    const [viewMode, setViewMode] = useState('card')
    const [orderBy, setOrderBy] = useState('comprehensive')
    const [pageNum, setPageNum] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const prevSearchIndexRef = useRef('')
    const isSearchChanged = useRef(false)
    const [prevSearchK, setPrevSearchK] = useState('')

    // 新增：管理分组数据和折叠状态
    const [expandedGroups, setExpandedGroups] = useState(new Set());

    // 新增：快速搜索卡片展开状态
    const [fastJieduOpen, setFastJieduOpen] = useState({});
    const [fastMindMapOpen, setFastMindMapOpen] = useState({});
    const [fastTimelineOpen, setFastTimelineOpen] = useState({});

    const pageNums = allData?.total_rows !== undefined && pageSize !== 0 ? Math.floor(allData?.total_rows / pageSize) + 1 : 1

    const [currentStep, setCurrentStep] = useState(0)

    const searchSteps = [
        { text: '识别解析搜索主题...', icon: <SparkSvg /> },
        { text: '海量数据精准检索...', icon: <Database size='1.5rem' /> },
        { text: '语句级别高亮定位...', icon: <SearchIcon size='1.5rem' /> },
        { text: '综合加权评分排序...', icon: <StarIcon size='1.5rem' /> }
    ]

    useEffect(() => {
        if (!loading) {
            setCurrentStep(0)
            return
        }

        let stepCount = 0
        let intervalTime = 1200
        if (searchK.length > 5) {
            intervalTime = 1800
        } else if (searchK.length > 10) {
            intervalTime = 2500
        }
        const interval = setInterval(() => {
            if (stepCount < searchSteps.length - 1) {
                stepCount++
                setCurrentStep(stepCount)
            } else {
                clearInterval(interval)
            }
        }, intervalTime)

        return () => clearInterval(interval)
    }, [loading, searchSteps.length, searchK])

    useEffect(() => {
        if (allData) {
            setPageNum(allData?.page_num)
            setPageSize(allData?.page_size)

        }
    }, [allData])

    useEffect(() => {
        setQuery({
            theme_id: '',
            search_content: ''
        })
    }, [])

    // Track search keyword changes
    useEffect(() => {
        if (searchK !== prevSearchK) {
            isSearchChanged.current = true
            setPrevSearchK(searchK)
        }
    }, [searchK, prevSearchK])

    // Track search index changes
    useEffect(() => {
        if (allData?.search_index && allData.search_index !== prevSearchIndexRef.current) {
            prevSearchIndexRef.current = allData.search_index
            isSearchChanged.current = false
        }
    }, [allData?.search_index])

    const selectCardAction = (item) => {
        if (selectedCards?.some(i => i?.policy_id === item.source_id)) {
            setSelectedCards([...selectedCards.filter(i => i?.policy_id !== item.source_id)])
        } else {
            setSelectedCards([...selectedCards, { policy_id: item.source_id, policy_title: item.policy_title || item.policy_name }])
        }
    }
    const selectAllAction = (e) => {
        if (e.target.checked) {
            setSelectedCards(data?.map(item => ({ policy_id: item.source_id, policy_title: item.policy_title || item.policy_name })))
        } else {
            setSelectedCards([])
        }
    }

    const toggleGroupExpansion = (groupIndex) => {
        const newExpandedGroups = new Set(expandedGroups);
        if (newExpandedGroups.has(groupIndex)) {
            newExpandedGroups.delete(groupIndex);
        } else {
            newExpandedGroups.add(groupIndex);
        }
        setExpandedGroups(newExpandedGroups);
    };

    const renderFileCard = (item, index) => {
        return (
            <div key={index} className="mb-4">
                <EnhancedFileCard
                    item={item}
                    index={index}
                    setFileName={setFileName}
                    setFileId={setFileId}
                    getPdfAction={getPdfAction}
                    setMdTxt={setMdTxt}
                    savedFoldListData={savedFoldListData}
                    setMarkData={setMarkData}
                    isSelected={selectedCards?.some(i => i?.policy_id === item?.source_id)}
                    onSelect={selectCardAction}
                    input={input}
                    pageNum={pageNum}
                    pageSize={pageSize}
                    mindmapVisible={true}
                    hightlight={true}
                    score={true}
                    state={state}
                    actions={actions}
                />
            </div>
        )
    }

    // 新增：渲染分组卡片
    const renderGroupedCard = (group, groupIndex, baseIndex) => {
        const { isGroup, items } = group;
        const isExpanded = expandedGroups.has(groupIndex);

        if (!isGroup) {
            // 单个项目，直接渲染EnhancedFileCard
            return (
                <div key={groupIndex} className="mb-4">
                    <EnhancedFileCard
                        item={items[0]}
                        index={baseIndex}
                        setFileName={setFileName}
                        setFileId={setFileId}
                        getPdfAction={getPdfAction}
                        setMdTxt={setMdTxt}
                        savedFoldListData={savedFoldListData}
                        setMarkData={setMarkData}
                        isSelected={selectedCards?.some(i => i?.policy_id === items[0]?.source_id)}
                        onSelect={selectCardAction}
                        input={input}
                        pageNum={pageNum}
                        pageSize={pageSize}
                        mindmapVisible={true}
                        hightlight={true}
                        score={true}
                        state={state}
                        actions={actions}
                    />
                </div>
            );
        }

        // 分组项目，渲染第一个和折叠按钮
        return (
            <div key={groupIndex}>
                {/* 第一个项目 */}
                <div className="mb-2">
                    <EnhancedFileCard
                        item={items[0]}
                        index={baseIndex}
                        setFileName={setFileName}
                        setFileId={setFileId}
                        getPdfAction={getPdfAction}
                        setMdTxt={setMdTxt}
                        savedFoldListData={savedFoldListData}
                        setMarkData={setMarkData}
                        isSelected={selectedCards?.some(i => i?.policy_id === items[0]?.source_id)}
                        onSelect={selectCardAction}
                        input={input}
                        pageNum={pageNum}
                        pageSize={pageSize}
                        mindmapVisible={true}
                        hightlight={true}
                        score={true}
                        state={state}
                        actions={actions}
                    />
                </div>

                {/* 折叠按钮 */}
                <div className="mb-4">
                    <button
                        className="btn btn-link btn-sm w-full"
                        onClick={() => toggleGroupExpansion(groupIndex)}
                    >
                        {isExpanded ? (
                            <>
                                <ChevronsUp size="16" />
                                <span>收起相同政策</span>
                            </>
                        ) : (
                            <>
                                <ChevronsDown size="16" />
                                <span>展开不同来源的相同政策 ({items.length - 1}条)</span>
                            </>
                        )}
                    </button>
                </div>

                {/* 展开的其他项目 */}
                {isExpanded && items.slice(1).map((item, index) => (
                    <div key={`${groupIndex}-${index + 1}`} className="ml-4 mb-4">
                        <EnhancedFileCard
                            item={item}
                            index={baseIndex + index + 1}
                            setFileName={setFileName}
                            setFileId={setFileId}
                            getPdfAction={getPdfAction}
                            setMdTxt={setMdTxt}
                            savedFoldListData={savedFoldListData}
                            setMarkData={setMarkData}
                            isSelected={selectedCards?.some(i => i?.policy_id === item?.source_id)}
                            onSelect={selectCardAction}
                            input={input}
                            pageNum={pageNum}
                            pageSize={pageSize}
                            mindmapVisible={true}
                            hightlight={true}
                            score={true}
                            state={state}
                            actions={actions}
                        />
                    </div>
                ))}
            </div>
        );
    };

    // 暂时注释掉批量操作栏，目前不需要
    /*
    const BatchOperationDiv = () => {
        return (
            <div className='flex gap-6 items-center'>
                <div className="form-control">
                    <label className="label cursor-pointer">
                        <div className='flex items-center gap-4'>
                            <input type="checkbox" className="checkbox" onChange={(e) => selectAllAction(e)} />
                            <span className="label-text">全选</span>
                        </div>
                    </label>
                </div>
                <span>已选：{selectedCards?.length ?? 0}</span>
                <button className="btn btn-sm" disabled={selectedCards?.length === 0 || getPdfLoading}
                    onClick={() => getPdfAction({ policy_info: selectedCards })}
                >
                    {getPdfLoading && <span className="loading loading-spinner"></span>}
                    批量下载
                </button>
            </div>
        )
    }
    */
    const BottomOperationDiv = () => {
        return (
            <div className='w-full bg-base-100 mb-2'>
                <div className='flex justify-between px-4 xl:px-8 items-center py-4'>
                    {/* 暂时注释掉批量操作栏
                    {BatchOperationDiv()}
                    */}
                    <div className='flex gap-6 justify-end items-center'>
                        {PageOperationDiv()}
                    </div>
                </div>
            </div>

        )
    }
    const PageOperationDiv = () => {
        return (
            <div className="join">
                <button className="join-item btn btn-sm btn-out" disabled={pageNum == 1} onClick={() => setQuery({ page_num: pageNum - 1 })}>«</button>
                <button className="join-item btn btn-sm cursor-default">{`${pageNum} / ${pageNums}`}</button>
                <button className="join-item btn btn-sm" disabled={pageNum === pageNums} onClick={() => setQuery({ page_num: pageNum + 1 })}>»</button>
            </div>
        )
    }
    const ResultsFilterDiv = () => {
        return (
            <div className='w-full bg-base-100 mb-2 z-20 shadow'>
                <div className='border-b flex justify-between px-4 xl:px-8 py-4 items-center'>
                    <span className="flex gap-2 items-center">
                        为您找到{allData?.total_rows ?? 0}条相关政策

                    </span>
                    <div className='flex gap-6'>
                        {PageOperationDiv()}
                        <select className="select select-bordered select-sm" value={pageSize} onChange={(e) => { setPageSize(e.target.value); setQuery({ page_size: Number(e.target.value) }) }}>
                            <option value='10'>10条每页</option>
                            <option value='20'>20条每页</option>
                            <option value='50'>50条每页</option>
                        </select>

                    </div>
                </div>
                <div className='flex justify-between px-4 xl:px-8 items-center py-4 border-b'>
                    {/* 暂时注释掉批量操作栏
                    {BatchOperationDiv()}
                    */}
                    <div className='flex gap-6 justify-end items-center'>
                        <div className="join">
                            <button
                                onClick={() => { setOrderBy('comprehensive'); setQuery({ order_by: 'comprehensive' }) }}
                                className={`btn join-item btn-sm ${orderBy === 'comprehensive' ? 'btn-active' : ''}`}>
                                综合检索
                            </button>
                            <button
                                onClick={() => { setOrderBy('relevance'); setQuery({ order_by: 'relevance' }) }}
                                className={`btn join-item btn-sm ${orderBy === 'relevance' ? 'btn-active' : ''}`}>
                                相关度 ↓
                            </button>
                            <button
                                onClick={() => { setOrderBy('time'); setQuery({ order_by: 'time' }) }}
                                className={`btn join-item btn-sm ${orderBy === 'time' ? 'btn-active' : ''}`}>
                                时效性 ↓
                            </button>
                        </div>

                        <div className="join">
                            <button
                                onClick={() => setViewMode('card')}
                                className={`btn join-item btn-sm ${viewMode === 'card' ? 'btn-active' : ''}`}>
                                <Grid size="16" />
                            </button>
                            <button
                                onClick={() => setViewMode('table')}
                                className={`btn join-item btn-sm ${viewMode === 'table' ? 'btn-active' : ''}`}>
                                <Menu size="16" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
    const TableDiv = (item, index) => {
        return (
            <tr>
                <td>
                    <label className="flex justify-center items-center">
                        <input type="checkbox" className="checkbox mr-2" checked={selectedCards?.some(i => i?.policy_id === item?.source_id)} onClick={() => selectCardAction(item)} />
                        {(pageNum - 1) * pageSize + index + 1}
                    </label>
                </td>
                <FileCard
                    getPdfAction={getPdfAction}
                    input={input}
                    type='row'
                    index={index}
                    item={item}
                    setFileId={setFileId}
                    fileName={fileName}
                    setFileName={setFileName}
                    setMarkData={setMarkData}
                    setMdTxt={setMdTxt}
                    savedFoldListData={savedFoldListData}
                    mindmapVisible
                    hightlight
                    score
                />
            </tr>
        )
    }

    // 移除前端过滤逻辑，数据过滤由后端处理
    // 直接使用后端返回的数据，不再进行前端过滤
    const groupedData = useMemo(() => {
        if (data && data.length > 0) {
            return groupSimilarPolicies(data);
        }
        return [];
    }, [data]);

    // expandedGroups只在tab或data变化时重置
    useEffect(() => {
        setExpandedGroups(new Set());
    }, [tab, data]);

    return (
        <div className="w-full">
            {/* Top Control Bar - Duplicated from bottom */}
            {ResultsFilterDiv()}
            
            <div className='w-full overflow-auto flex flex-col-reverse' id="fileSearchScrollDiv">
                {
                    loading && data?.length === 0 ? (
                        <div className="w-full text-center py-4">
                            <span className="loading loading-spinner"></span>
                        </div>
                    ) :
                        <>
                        </>
                }

                {
                    loading || (data?.length > 0) ?
                        <>
                            <div className="w-full">
                                <div className="my-2 flex flex-col-reserve">
                                    {
                                        viewMode === 'card' ?
                                            <>
                                                {
                                                    loading ?
                                                        <div className="w-full">
                                                            <div className="flex flex-col-reserve">
                                                                {[1, 2, 3, 4, 5].map((item, index) => (
                                                                    <div className="card bg-base-100 border-b w-full mb-4">
                                                                        <div className="card-body px-12 py-8">
                                                                            <LoadingCardDiv />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div> :
                                                        <>
                                                            {groupedData.map((group, groupIndex) => {
                                                                // 计算当前组前面所有项目的数量作为基础索引
                                                                let baseIndex = 0;
                                                                for (let i = 0; i < groupIndex; i++) {
                                                                    baseIndex += groupedData[i].items.length;
                                                                }
                                                                return renderGroupedCard(group, groupIndex, baseIndex);
                                                            })}
                                                        </>
                                                }
                                            </>
                                            :
                                            <div className="overflow-x-auto p-4 border bg-base-100">
                                                <table className="table ">
                                                    <thead>
                                                        <tr>
                                                            <th>序号</th>
                                                            <th>标题</th>
                                                            <th>政策来源</th>
                                                            <th>标签</th>
                                                            <th>
                                                                <div className={`cursor-pointer ${orderBy === 'relevance' ? 'text-primary' : ''}`} onClick={() => { setOrderBy('relevance'); setQuery({ order_by: 'relevance' }) }}>相关度 ↓</div>
                                                            </th>
                                                            <th>
                                                                <div className={`cursor-pointer ${orderBy === 'time' ? 'text-primary' : ''}`} onClick={() => { setOrderBy('time'); setQuery({ order_by: 'time' }) }}>时效性 ↓</div>
                                                            </th>
                                                            <th>操作</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {data?.map((item, index) => (
                                                            <>
                                                                {TableDiv(item, index)}
                                                            </>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                    }
                                </div>
                            </div>
                        </>
                        :
                        <div className="w-full py-8 text-center">
                            暂无检索结果
                        </div>
                }
            </div>
        </div>
    )
}